import lodash from "lodash";
import { normalizeClientPhone } from "./clientPhone.mjs";

export function createReservationDraft(date, locale, defaults = {}) {
    return lodash.merge(
        {
            reservation_date: date,
            waiting_id: null,
            nb_pers: 2,
            nb_children: 0,
            slot_id: null,
            slot: null,
            service: null,
            choosen_menus: {},
            choosen_general_options: {},
            restaurant_comment: "",
            num_table: null,
            option_bank: null,
            amount_to_pay: 0,
            send_confirmation: true,
            gift: null,
            user_id: null,
            duration: { HH: "01", mm: "00" },
            is_duration_customized: false,
            client: {
                id: null,
                civility: null,
                firstname: null,
                lastname: null,
                company: null,
                email: null,
                newsletter: false,
                type: null,
                is_vip: false,
                locale,
                phone: { raw: null, isValid: null, country: null, value: null },
            },
            room_number: null,
            ignore_placement: false,
            is_hotel_client: false,
        },
        lodash.cloneDeep(defaults ?? {})
    );
}

export function hasValidGuestCounts(reservation) {
    return (
        Number.isInteger(reservation.nb_pers) && reservation.nb_pers > 0 && Number.isInteger(reservation.nb_children) && reservation.nb_children >= 0
    );
}

export function guestCount(reservation) {
    return Number(reservation.nb_pers || 0) + Number(reservation.nb_children || 0);
}

export function requiredClientFields(fields, notificationChannel) {
    const required = Object.fromEntries(fields.map((field) => [field.field_name, Number(field.pivot?.is_mandatory) === 1]));
    if (notificationChannel === "mail") required.email = true;
    if (notificationChannel === "sms") required.tel = true;
    return required;
}

export function clientDisplayName(client) {
    const name = [client.firstname, client.lastname]
        .filter(Boolean)
        .map((part) => part.trim())
        .filter(Boolean)
        .join(" ");
    return name || client.company || client.email || client.phone?.raw || (client.id ? `#${client.id}` : "");
}

export function serializeMenus(menus) {
    if (!Array.isArray(menus)) return lodash.cloneDeep(menus ?? {});
    return Object.fromEntries(
        menus
            .filter((menu) => Number(menu.quantity) > 0)
            .map((menu) => [
                `id_${menu.id}`,
                {
                    id: menu.id,
                    value: Number(menu.quantity),
                    options: Object.fromEntries(
                        (menu.options ?? [])
                            .filter((option) => Number(option.quantity) > 0)
                            .map((option) => [`id_${option.id}`, { id: option.id, value: Number(option.quantity) }])
                    ),
                },
            ])
    );
}

export function selectedTables(entities, pax) {
    const tables = entities.flatMap((entity) => (entity.type === "group" ? entity.tables : [entity]));
    return [...new Map(tables.map((table) => [table.id, { id: table.id, pax }])).values()];
}

export function reservationExtrasTotal(reservation) {
    const itemTotal = (item) => Number(item.price || 0) * Number(item.quantity ?? item.value ?? 0);
    return (
        Object.values(reservation.choosen_menus ?? {})
            .filter((menu) => Number(menu.quantity ?? menu.value ?? 0) > 0)
            .reduce(
                (total, menu) => total + itemTotal(menu) + Object.values(menu.options ?? {}).reduce((sum, option) => sum + itemTotal(option), 0),
                0
            ) + Object.values(reservation.choosen_general_options ?? {}).reduce((total, option) => total + itemTotal(option), 0)
    );
}

export function buildMenuChoices(available, reservation) {
    const quantity = (item) => Number(item?.quantity ?? item?.value ?? item?.nb ?? 0);
    const current = Object.values(reservation.choosen_menus ?? {});
    return available.map((menu) => {
        const selected = current.find((item) => item.id === menu.id) ?? reservation.back_choosen_menus?.find((item) => item.id === menu.id);
        const selectedOptions = Object.values(selected?.options?.data ?? selected?.options ?? {});
        return {
            id: menu.id,
            name: menu.name,
            description: menu.description,
            price: menu.price,
            quantity: quantity(selected),
            options: (menu.menu_options?.data ?? []).map((option) => ({
                id: option.id,
                name: option.name,
                description: option.description,
                price: option.price,
                quantity_type: option.quantity_type,
                quantity: quantity(selectedOptions.find((item) => item.id === option.id)),
            })),
        };
    });
}

export function limitMenuChoices(menus, guests) {
    let remaining = Math.max(0, Number(guests) || 0);
    menus.forEach((menu) => {
        menu.quantity = Math.min(remaining, Math.max(0, Number(menu.quantity) || 0));
        remaining -= menu.quantity;
        menu.options.forEach((option) => {
            if (!menu.quantity) option.quantity = 0;
            else if (option.quantity_type === "mandatory_for_each" && Number(option.quantity) > 0) option.quantity = menu.quantity;
            else if (option.quantity_type === "limited_by_pers") option.quantity = Math.min(menu.quantity, Number(option.quantity) || 0);
        });
    });
}

export function reservationPayload(reservation, restaurantId, entities, idempotencyKey, option = false) {
    const phone = normalizeClientPhone(reservation.client.phone.raw, reservation.client.phone.country);
    const fields = [
        "waiting_id",
        "nb_pers",
        "nb_children",
        "slot_id",
        "restaurant_comment",
        "num_table",
        "option_bank",
        "amount_to_pay",
        "send_confirmation",
        "gift",
        "user_id",
        "duration",
        "is_duration_customized",
        "room_number",
        "ignore_placement",
        "is_hotel_client",
    ];
    return {
        ...Object.fromEntries(fields.map((field) => [field, reservation[field]])),
        ...lodash.pick(reservation.client, ["civility", "firstname", "lastname", "company", "email", "newsletter", "type", "is_vip", "locale"]),
        client: lodash.cloneDeep(reservation.client),
        client_id: reservation.client.id,
        phoneNumber: phone.number,
        phoneCountry: phone.country,
        reservation_date: reservation.reservation_date.toISODate(),
        restaurant_id: restaurantId,
        choosen_menus: serializeMenus(reservation.choosen_menus),
        choosen_general_options: lodash.cloneDeep(reservation.choosen_general_options),
        tables: selectedTables(entities, guestCount(reservation)),
        stripeToken: false,
        option,
        idempotency_key: idempotencyKey,
    };
}

export function waitingPayload(reservation, restaurantId) {
    const phone = normalizeClientPhone(reservation.client.phone.raw, reservation.client.phone.country);
    return {
        ...lodash.pick(reservation.client, ["civility", "firstname", "lastname", "company", "email", "newsletter", "type", "is_vip", "locale"]),
        client_id: reservation.client.id,
        phone_number: phone.number,
        phone_country: phone.country,
        reservation_date: reservation.reservation_date.toISODate(),
        nb_pers: reservation.nb_pers,
        nb_children: reservation.nb_children,
        slot_id: reservation.slot_id,
        restaurant_id: restaurantId,
        restaurant_comment: reservation.restaurant_comment,
        room_number: reservation.room_number,
        choosen_menus: serializeMenus(reservation.choosen_menus),
        choosen_general_options: lodash.cloneDeep(reservation.choosen_general_options),
    };
}

export function createRequestGate() {
    let active = null;
    return {
        begin() {
            active?.abort();
            const controller = new AbortController();
            active = controller;
            return { signal: controller.signal, current: () => active === controller && !controller.signal.aborted };
        },
        cancel() {
            active?.abort();
            active = null;
        },
    };
}

/**
 * Why the placement step does not allow saving yet, or null when it does.
 * @returns {"busy"|"loadFailed"|"tableUnavailable"|"tableRequired"|null}
 */
export function placementBlocker({ busy, loadFailed, tableUnavailable, ignorePlacement, hasSelectedTables, canPlace }) {
    if (busy) return "busy";
    if (loadFailed) return "loadFailed";
    if (tableUnavailable) return "tableUnavailable";
    if (!ignorePlacement && !hasSelectedTables && !canPlace) return "tableRequired";
    return null;
}

/**
 * First reason preventing the reservation from being saved, in the order the user can act on it,
 * or null when the form can be submitted.
 * @returns {"loading"|"loadFailed"|"slot"|"guests"|"waitingFiles"|"tableUnavailable"|"tableRequired"|null}
 */
export function submitBlocker({ loading, loadFailed, hasSlot, validGuests, waiting, hasFiles, placementReady, placementReason }) {
    if (loading) return "loading";
    if (loadFailed) return "loadFailed";
    if (!hasSlot) return "slot";
    if (!validGuests) return "guests";
    if (waiting) return hasFiles ? "waitingFiles" : null;
    if (placementReady) return null;
    if (placementReason === "busy" || !placementReason) return "loading";
    return placementReason;
}

/**
 * Reuses a legacy "Label :" translation as a heading.
 */
export function withoutTrailingColon(label) {
    return String(label ?? "").replace(/\s*:\s*$/, "");
}
